import { Reference } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";

import MedicationQuestion from "@components/MedicationQuestion";
import TrackerModal from "@components/TrackerModal";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import { Opener } from "@hooks/useOpener";
import {
  AssessmentStepMarkCompletedDocument,
  AssessmentStepMarkCompletedMutation,
  AssessmentStepMarkCompletedMutationVariables,
  AssessmentStepSkipReasonEnum,
  AssessmentStepTypeEnum,
  AssessmentTypeEnum,
  ConsultFragment as Consult,
  FileResourceFragmentDoc,
  GetJourneyWithCareProfileQuery,
  MedicationFragment as Medication,
  TrackerStatusEnum,
  TrackerStatusUpdateDocument,
  TrackerStatusUpdateMutation,
  TrackerStatusUpdateMutationVariables,
  TrackerTypeEnum
} from "@typing/Generated";
import { MedicationGroups } from "@typing/Models";
import { journeyRoute } from "@utils/routeUtils";

type Assessment = GetJourneyWithCareProfileQuery["journey"]["assessments"][number];

type Journey = {
  assessments: Assessment[];
  consults: Consult[];
  id: string;
  medicationTrackerStatus: TrackerStatusEnum;
  medications: Medication[];
};

type Props = {
  batchedMedications: MedicationGroups;
  journey: Journey;
  medicationTrackerOpener: Opener;
  restartWizard?: boolean;
};

const MedicationTracker = ({ batchedMedications, journey, medicationTrackerOpener, restartWizard = false }: Props) => {
  const [questionIndex, setQuestionIndex] = useState<number | null>(restartWizard ? 0 : null);
  const [skipReason, setSkipReason] = useState<AssessmentStepSkipReasonEnum | null>(null);
  const history = useHistory();
  const myClient = useMyClient();

  const questionOrder = useMemo(
    () => [
      AssessmentStepTypeEnum.START,
      AssessmentStepTypeEnum.PRESCRIPTIONS,
      AssessmentStepTypeEnum.OVER_THE_COUNTER,
      AssessmentStepTypeEnum.VITAMINS_AND_SUPPLEMENTS
    ],
    []
  );

  const orderedQuestions = useMemo(() => {
    if (journey.assessments) {
      const medicationTrackerAssessment = journey.assessments.find(
        assessment => assessment.type === AssessmentTypeEnum.MEDICATION_TRACKER
      );
      return questionOrder.map(step =>
        medicationTrackerAssessment?.steps.find(medicationStep => medicationStep.step === step)
      );
    }
    return [];
  }, [journey, questionOrder]);

  const handleSkipReasonToggle = useCallback(
    (reason: AssessmentStepSkipReasonEnum) => {
      if (skipReason === reason) {
        setSkipReason(null);
      } else {
        setSkipReason(reason);
      }
    },
    [skipReason]
  );

  const handleOpenMessages = useCallback(() => {
    medicationTrackerOpener.close();
    history.push(
      journeyRoute({
        journeyId: journey.id,
        linkAfterJourney: `/messages`,
        myClient
      })
    );
  }, [history, journey.id, medicationTrackerOpener, myClient]);

  useEffect(() => {
    if (questionIndex === null && orderedQuestions && orderedQuestions.length > 0) {
      const firstUnanswered = orderedQuestions.find(question => !question?.completedAt);
      if (firstUnanswered) {
        setQuestionIndex(orderedQuestions.indexOf(firstUnanswered));
      } else {
        setQuestionIndex(orderedQuestions.length);
      }
    }
  }, [orderedQuestions, questionIndex, medicationTrackerOpener]);

  const nextAllowed = useMemo(
    () =>
      skipReason !== null ||
      (questionIndex === 1 && batchedMedications?.prescription.length > 0) ||
      (questionIndex === 2 && batchedMedications?.overTheCounter.length > 0) ||
      (questionIndex === 3 && batchedMedications?.vitaminAndSupplement.length > 0) ||
      questionIndex === 0,
    [questionIndex, batchedMedications, skipReason]
  );

  const backAllowed = useMemo(() => {
    if (questionIndex && questionIndex > 0) {
      return true;
    }
    return false;
  }, [questionIndex]);

  const handleBack = useCallback(() => {
    if (backAllowed && questionIndex) {
      setQuestionIndex(questionIndex - 1);
    }
  }, [backAllowed, questionIndex]);

  const [markAssessmentStepCompleted] = useMutationWithValidation<
    AssessmentStepMarkCompletedMutation,
    AssessmentStepMarkCompletedMutationVariables
  >(AssessmentStepMarkCompletedDocument, "assessmentStep");

  const [updateTrackerStatus] = useMutationWithValidation<
    TrackerStatusUpdateMutation,
    TrackerStatusUpdateMutationVariables
  >(TrackerStatusUpdateDocument, "tracker", {
    update: (store, { data }) => {
      if (data?.trackerStatusUpdate?.fileResource) {
        store.modify({
          fields: {
            fileResources(cachedFileResourceRefs: readonly Reference[]) {
              const newFileResourceRef = store.writeFragment({
                data: data.trackerStatusUpdate?.fileResource,
                fragment: FileResourceFragmentDoc,
                fragmentName: "FileResource"
              });
              if (newFileResourceRef && data.trackerStatusUpdate?.fileResource) {
                return [...cachedFileResourceRefs, newFileResourceRef];
              }
              return [...cachedFileResourceRefs];
            }
          },
          id: store.identify({ ...journey })
        });
      }
    }
  });

  const [completeTracker] = useMutationWithValidation<
    TrackerStatusUpdateMutation,
    TrackerStatusUpdateMutationVariables
  >(TrackerStatusUpdateDocument, "tracker", {
    update: (store, { data }) => {
      store.modify({
        fields: {
          medicationTrackerStatus() {
            return TrackerStatusEnum.COMPLETED;
          }
        },
        id: store.identify({ ...journey })
      });
      if (data?.trackerStatusUpdate?.fileResource) {
        store.modify({
          fields: {
            fileResources(cachedFileResourceRefs: readonly Reference[]) {
              const newFileResourceRef = store.writeFragment({
                data: data.trackerStatusUpdate?.fileResource,
                fragment: FileResourceFragmentDoc,
                fragmentName: "FileResource"
              });
              if (newFileResourceRef && data.trackerStatusUpdate?.fileResource) {
                return [...cachedFileResourceRefs, newFileResourceRef];
              }
              return [...cachedFileResourceRefs];
            }
          },
          id: store.identify({ ...journey })
        });
      }
    }
  });

  const handleUpdate = useCallback(() => {
    if (questionIndex !== null) {
      const currentQuestion = orderedQuestions[questionIndex];
      if (questionIndex < questionOrder.length) {
        if (currentQuestion?.completedAt) {
          setQuestionIndex(questionIndex + 1);
          setSkipReason(null);
        } else if (currentQuestion) {
          markAssessmentStepCompleted({
            id: currentQuestion.id,
            skipReason
          }).then(() => {
            if (
              currentQuestion.step === AssessmentStepTypeEnum.VITAMINS_AND_SUPPLEMENTS &&
              journey.medicationTrackerStatus !== TrackerStatusEnum.COMPLETED
            ) {
              completeTracker({
                journeyId: journey.id,
                status: TrackerStatusEnum.COMPLETED,
                type: TrackerTypeEnum.MEDICATION_TRACKER
              });
            } else if (journey.medicationTrackerStatus === TrackerStatusEnum.NOT_STARTED) {
              updateTrackerStatus({
                journeyId: journey.id,
                status: TrackerStatusEnum.STARTED,
                type: TrackerTypeEnum.MEDICATION_TRACKER
              });
            }
            setQuestionIndex(questionIndex + 1);
            setSkipReason(null);
          });
        }
      }
      if (questionIndex === questionOrder.length) {
        medicationTrackerOpener.close();
      }
    }
  }, [
    questionIndex,
    orderedQuestions,
    questionOrder.length,
    markAssessmentStepCompleted,
    skipReason,
    journey.medicationTrackerStatus,
    journey.id,
    completeTracker,
    updateTrackerStatus,
    medicationTrackerOpener
  ]);

  return (
    <TrackerModal
      backAllowed={backAllowed}
      data-test="medication-tracker-modal"
      handleBack={handleBack}
      handleUpdate={handleUpdate}
      headerKey="pages.client.medications.sections.header"
      hideCurrentSection={questionIndex === questionOrder.length + 1}
      hideImage
      name="MedicationTracker"
      nextAllowed={nextAllowed}
      opener={medicationTrackerOpener}
      questionIndex={questionIndex}
      sectionCount={questionOrder.length + 1}
    >
      <>
        {questionIndex !== null && (
          <MedicationQuestion
            backAllowed={backAllowed}
            batchedMedications={batchedMedications}
            consults={journey.consults}
            handleBack={handleBack}
            handleOpenMessages={handleOpenMessages}
            handleSkipReasonToggle={handleSkipReasonToggle}
            journeyId={journey.id}
            nextAllowed={nextAllowed}
            onUpdate={handleUpdate}
            question={orderedQuestions[questionIndex]}
            skipReason={skipReason}
          />
        )}
      </>
    </TrackerModal>
  );
};

export default MedicationTracker;
